import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  getThemeFontImport,
  resolveProposalTheme,
} from '../components/proposals-standalone/proposalThemes';

const initialForm = {
  schoolName: '',
  contactName: '',
  email: '',
  phone: '',
  ageRange: '',
  goal: '',
  period: '',
  notes: '',
};

export default function SchoolProposalRequestForm() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const theme = resolveProposalTheme('school');

  const selectedProject = useMemo(
    () => ({
      documentId: searchParams.get('projectId') || '',
      title: searchParams.get('projectTitle') || '',
    }),
    [searchParams]
  );

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const response = await fetch('/api/school-proposals/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          projectDocumentId: selectedProject.documentId || null,
          projectTitle: selectedProject.title || null,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Invio richiesta non riuscito');
      }
      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      setError(err.message || 'Errore durante invio richiesta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box bg={theme.colors.bg} minH="100vh" py={{ base: 10, md: 14 }}>
      <Helmet>
        <title>Richiesta proposte scuole - CIL</title>
        <link rel="stylesheet" href={getThemeFontImport(theme.key)} />
      </Helmet>
      <Container maxW="container.lg" mb={6}>
        <Stack direction="row" justify="space-between" align="center">
          <Button
            as={RouterLink}
            to="/proposte-scuole"
            size="sm"
            variant="link"
            color={theme.colors.text}
            fontWeight="700"
            fontFamily={theme.fontBody}
          >
            Proposte CIL
          </Button>
        </Stack>
      </Container>
      <Container maxW="container.md">
        <Stack spacing={6}>
          <Heading
            as="h1"
            size="lg"
            color={theme.colors.text}
            fontFamily={theme.fontHeading}
          >
            Richiesta proposta scuole
          </Heading>
          {selectedProject.title ? (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              Proposta selezionata: <strong>{selectedProject.title}</strong>
            </Alert>
          ) : null}
          {error ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          ) : null}
          {success ? (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              Richiesta inviata con successo. Ti contatteremo al più presto.
            </Alert>
          ) : null}

          <Box
            as="form"
            onSubmit={onSubmit}
            bg={theme.colors.surface}
            p={6}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={theme.colors.border}
          >
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Istituto</FormLabel>
                <Input
                  name="schoolName"
                  value={form.schoolName}
                  onChange={onChange}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Referente</FormLabel>
                <Input
                  name="contactName"
                  value={form.contactName}
                  onChange={onChange}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Telefono</FormLabel>
                <Input name="phone" value={form.phone} onChange={onChange} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Fascia età / classe</FormLabel>
                <Input
                  name="ageRange"
                  value={form.ageRange}
                  onChange={onChange}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Obiettivo educativo</FormLabel>
                <Textarea
                  name="goal"
                  value={form.goal}
                  onChange={onChange}
                  rows={3}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Periodo desiderato</FormLabel>
                <Input name="period" value={form.period} onChange={onChange} />
              </FormControl>
              <FormControl>
                <FormLabel>Note aggiuntive</FormLabel>
                <Textarea
                  name="notes"
                  value={form.notes}
                  onChange={onChange}
                  rows={4}
                />
              </FormControl>
              <Button
                type="submit"
                bg="#ffcc00"
                color="black"
                isDisabled={loading}
              >
                {loading ? (
                  <Stack direction="row" align="center">
                    <Spinner size="sm" />
                    <Text>Invio in corso...</Text>
                  </Stack>
                ) : (
                  'Invia richiesta'
                )}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
